import type { BaseDocument, Ref } from "./data";
import type { EvalMeasurement } from "./evaluation";
import type { Event } from "./event";
import type { Installation } from "./installation";
import type { Measurement } from "./measurement";
import type { GeminiUsageSnapshot } from "./plausibility";
import type { Sensor } from "./sensor";
import type { Verification } from "./verification";

/** Staff actions on one proposal, as `POST .../proposals/{id}/{action}`. */
export enum SceneFactProposalAction {
	Apply = "apply",
	Redraft = "redraft",
	Reject = "reject",
	Revert = "revert",
}

/**
 * Where a proposal came from. `reviewer` is a staff note typed in the Shadow
 * Realm; `customerEdit` is drafted automatically when a customer rewrites an
 * alert's title, description or severity on the dashboard; `revert` is the
 * revision written when staff undo the currently applied facts.
 */
export enum SceneFactProposalSource {
	CustomerEdit = "customerEdit",
	Revert = "revert",
	Reviewer = "reviewer",
}

/**
 * Lifecycle of one proposal. The same collection is the review queue and the
 * camera's revision history, so the status says which of the two a row is in.
 *
 * - `drafting`: queued for (or running in) the draft worker.
 * - `pending`: drafted and waiting for staff.
 * - `noChange`: the note held nothing physical to learn.
 * - `failed`: the draft errored, or its output kept breaking the directive guard.
 * - `applied`: written to `Sensor.sceneFacts`; now a revision in the history.
 * - `rejected`: staff declined it.
 * - `superseded`: a later customer edit of the same event replaced it.
 * - `reverted`: it was the applied revision until staff reverted it.
 */
export enum SceneFactProposalStatus {
	Applied = "applied",
	Drafting = "drafting",
	Failed = "failed",
	NoChange = "noChange",
	Pending = "pending",
	Rejected = "rejected",
	Reverted = "reverted",
	Superseded = "superseded",
}

/**
 * The two sides of a re-test. Both run the camera's real merged config through
 * the real first-pass worker on the same clip; they differ only in the scene
 * reference — today's, or today's with the proposed facts in place.
 */
export enum SceneFactRetestArm {
	Current = "current",
	Proposed = "proposed",
}

export enum SceneFactRetestArmStatus {
	Completed = "completed",
	Failed = "failed",
	Pending = "pending",
}

export type CreateSceneFactProposalParams = Omit<
	SceneFactProposal,
	| "createdAt"
	| "event"
	| "id"
	| "installation"
	| "measurement"
	| "sensor"
	| "updatedAt"
	| "verification"
> & {
	event?: string;
	installation: string;
	measurement?: string;
	sensor: string;
	verification?: string;
};

/** The customer's edit that triggered a `customerEdit` proposal. */
export interface SceneFactCustomerEdit {
	/** Display name of the editor, from `event.updated`; absent when unresolved. */
	actorName?: string;
	/** The changed fields exactly as `event.updated` carried them. */
	changes: Record<string, unknown>;
	/**
	 * What the alert said before the edit. Optional because `event.updated`
	 * carries only the new values; the service fills in what it can recover.
	 */
	previous?: SceneFactCustomerEditPrevious;
}

export interface SceneFactCustomerEditPrevious {
	description?: string;
	severity?: number;
	title?: string;
}

/**
 * A proposed change to one camera's human-taught scene facts, drafted by an
 * LLM from a note about a clip. Tenant database.
 *
 * Facts are DESCRIPTIVE physical layout only — what lies beyond each frame
 * edge, where the doors and exits are. Never a directive: "watch for" or
 * "ignore" wording was measured driving false alerts from about 0/hr to
 * 16.4/hr, which is why every draft and every staff edit is run through
 * `findDirectivePhrase` before it can be applied.
 *
 * An applied row is also a revision: `previousFacts` is what it replaced and
 * `appliedFacts` what it wrote, so history and revert need no second
 * collection.
 */
export interface SceneFactProposal extends BaseDocument {
	appliedAt?: Date;
	/** Kinde `sub` of the staff member who applied it. */
	appliedBy?: string;
	/** The facts actually written, after any staff edit and normalization. */
	appliedFacts?: string;
	/**
	 * `Sensor.sceneFacts` when the draft ran ("" when the camera had none).
	 * Apply refuses when the sensor has moved on since, so a stale draft can
	 * never silently overwrite a newer revision.
	 */
	baseFacts?: string;
	customerEdit?: SceneFactCustomerEdit;
	/** Why drafting failed, including the directive phrase the guard caught. */
	error?: string;
	event?: Ref<Event>;
	installation: Ref<Installation>;
	/**
	 * The detection on the clip the note is about — what Stage 1 claimed, and
	 * the clip's media domain and time range. Kept from the request so every
	 * later read is one `findById`, never a scan of the camera's history.
	 */
	measurement?: Ref<Measurement>;
	/** S3 key of the clip the note is about, in `VideoUploads`. */
	mediaKey: string;
	/** Gemini model the draft ran on. */
	model?: string;
	/** The reviewer's words, or a rendered summary of the customer's edit. */
	note: string;
	/** `Sensor.sceneFacts` immediately before this revision was applied. */
	previousFacts?: string;
	/** The full revised facts text the model returned (merged, not appended). */
	proposedFacts?: string;
	/** One sentence for the reviewer on what changed and why. */
	reasoning?: string;
	rejectedAt?: Date;
	/** Kinde `sub` of the staff member who rejected it. */
	rejectedBy?: string;
	rejectedReason?: string;
	/** Kinde `sub` of the staff member who asked for the draft, when staff did. */
	requestedBy?: string;
	/**
	 * The latest re-test of this proposal. Starting another replaces it whole,
	 * and a redraft clears it: a re-test describes one draft.
	 */
	retest?: SceneFactProposalRetest;
	/** For a `revert` revision: the applied revision it undid. */
	revertOf?: Ref<SceneFactProposal>;
	sensor: Ref<Sensor>;
	source: SceneFactProposalSource;
	status: SceneFactProposalStatus;
	usage?: GeminiUsageSnapshot;
	verification?: Ref<Verification>;
}

/**
 * A before/after run of one proposal on the real detector, embedded on the
 * proposal it tests. The scene-facts worker fills in each arm as the eval
 * worker reports it.
 *
 * One sample per arm. Model output varies run to run, so a re-test is a sanity
 * check on the proposal, not a measurement of it.
 */
export interface SceneFactProposalRetest {
	arms: SceneFactRetestArmResult[];
	/**
	 * Minted per re-test and carried in the eval jobs' `evalResultId`. A result
	 * is written only while it still matches, so a late message from a re-test
	 * that has since been replaced (or cleared by a redraft) lands nowhere.
	 */
	id: string;
	requestedAt: Date;
	/** Kinde `sub` of the staff member who started the re-test. */
	requestedBy?: string;
}

/** One arm's outcome, filled in by the scene-facts worker as it lands. */
export interface SceneFactRetestArmResult {
	arm: SceneFactRetestArm;
	/** Summed Gemini cost (USD) for the arm; null when the model is unpriced. */
	costUsd?: null | number;
	error?: string;
	/** Model the first pass ran on, as the worker reported it. */
	firstPassModel?: string;
	/** First-pass output, with enhancements merged as production merges them. */
	measurements: EvalMeasurement[];
	/** The exact `sceneDescription` this arm sent; absent when it sent none. */
	sceneReference?: string;
	status: SceneFactRetestArmStatus;
}
